"use client";

import { useEffect, useState } from "react";
import type { Locale } from "@/i18n-config";
import { scrollArticleTo } from "./utils";

type TocItem = { id: string; title: string; level: number };

export default function TableOfContents({
  items,
  lang,
}: {
  items: TocItem[];
  lang: Locale;
}) {
  const isFa = lang === "fa";
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-100px 0px -65% 0px", threshold: 0 }
    );

    const h2Only = items.filter((i) => i.level === 2);
    for (const item of h2Only) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [items]);

  if (!items.length) return null;

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Exact even with the article's blocks laid out lazily (see scrollArticleTo).
    scrollArticleTo(el, 100);
  };

  // Build nested structure: match h3 to parent h2 by title numbering
  // e.g. h3 "1.1. Why..." belongs to h2 "1. Introduction..."
  // e.g. h3 "۱.۱. چرا..." belongs to h2 "۱. مقدمه..."
  function getSectionNum(title: string): string | null {
    // Match patterns like "1.", "1.1.", "۱.", "۱.۱." at the start
    const m = title.match(/^[\s]*([\d۰-۹]+)/);
    return m ? m[1] : null;
  }

  // Normalize Persian digits to Latin for comparison
  function normNum(s: string): string {
    return s.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));
  }

  const h2Items = items.filter((i) => i.level === 2);
  const h3Items = items.filter((i) => i.level === 3);

  const h3sByParent = new Map<string, TocItem[]>();
  for (const h3 of h3Items) {
    const h3Num = getSectionNum(h3.title);
    if (!h3Num) continue;
    const h3Normalized = normNum(h3Num);
    // "1.1" → parent section "1", "2.3" → parent section "2"
    const parentSectionNum = h3Normalized.split(".")[0] || h3Normalized;

    const parent = h2Items.find((h2) => {
      const h2Num = getSectionNum(h2.title);
      if (!h2Num) return false;
      return normNum(h2Num) === parentSectionNum;
    });

    if (parent) {
      const list = h3sByParent.get(parent.id) || [];
      list.push(h3);
      h3sByParent.set(parent.id, list);
    }
  }

  return (
    <nav dir={isFa ? "rtl" : "ltr"} className="v2-toc" aria-label={isFa ? "فهرست مطالب" : "Table of Contents"}>
      <h3>{isFa ? "فهرست مطالب" : "Table of Contents"}</h3>
      <ul>
        {h2Items.map((h2) => {
          const children = h3sByParent.get(h2.id) || [];
          return (
            <li key={h2.id}>
              <button type="button" className="v2-focus" onClick={() => scrollTo(h2.id)} aria-current={activeId === h2.id ? "location" : undefined}>
                {h2.title}
              </button>
              {children.length > 0 && (
                <ul>
                  {children.map((h3) => (
                    <li key={h3.id}>
                      <button type="button" className="v2-focus" onClick={() => scrollTo(h3.id)} aria-current={activeId === h3.id ? "location" : undefined}>
                        {h3.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
